import { useState, useEffect, useCallback } from "react";
import { UserPlus, Users, Trash2, Shield, User, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { teamApi } from "@/utils/api";

const TeamPage = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({ name: "", last_name: "", email: "" });
  const [sending, setSending] = useState(false);
  const [currentUser, setCurrentUser] = useState({});

  const fetchTeam = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem("admin_user") || "{}");
    setCurrentUser(user);
    try {
      const res = await teamApi.getAll();
      setTeam(res.data);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    
    try {
      await teamApi.create(formData);
      toast.success("Invitación enviada por email");
      setShowDialog(false);
      setFormData({ name: "", last_name: "", email: "" });
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar invitación");
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este usuario?")) return;
    try {
      await teamApi.delete(id);
      toast.success("Usuario eliminado");
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const isAdmin = currentUser.role === "admin";

  return (
    <AdminLayout>
      <div className="space-y-6 pb-20" data-testid="team-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-white">Equipo</h1>
            <p className="text-[#A3A3A3] text-sm">Gestiona los usuarios del sistema</p>
          </div>
          {isAdmin && (
            <Button onClick={() => { setFormData({ name: "", last_name: "", email: "" }); setShowDialog(true); }}
              className="bg-[#D4AF37] text-black hover:bg-[#FFE082]" data-testid="add-user-btn">
              <UserPlus size={18} className="mr-2" />Invitar Usuario
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {team.map((member) => (
              <Card key={member.id} className="bg-[#121212] border-white/5">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                        <span className="text-[#D4AF37] font-semibold text-lg">{member.name?.charAt(0)?.toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-white font-medium">{member.name} {member.last_name || ''}</p>
                        <p className="text-[#A3A3A3] text-sm">{member.email}</p>
                        <div className="flex items-center gap-1 mt-1">
                          {member.role === "admin" ? <Shield size={14} className="text-[#D4AF37]" /> : <User size={14} className="text-[#A3A3A3]" />}
                          <span className={`text-xs ${member.role === "admin" ? "text-[#D4AF37]" : "text-[#A3A3A3]"}`}>
                            {member.role === "admin" ? "Administrador" : "Agente"}
                          </span>
                          {!member.password_hash && member.invite_token && (
                            <span className="ml-2 text-xs text-amber-500">(Pendiente)</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {isAdmin && member.id !== currentUser.id && (
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(member.id)} className="text-[#A3A3A3] hover:text-red-500">
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {team.length === 0 && !loading && (
          <div className="text-center py-12 bg-[#121212] rounded-lg border border-white/5">
            <Users size={48} className="mx-auto text-[#A3A3A3] mb-4" />
            <p className="text-white text-lg mb-2">No hay usuarios</p>
            <p className="text-[#A3A3A3]">Invita miembros a tu equipo</p>
          </div>
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Mail className="text-[#D4AF37]" size={20} />
                Invitar Nuevo Usuario
              </DialogTitle>
            </DialogHeader>
            <p className="text-[#A3A3A3] text-sm">
              Se enviará un correo electrónico al usuario con un enlace para configurar su contraseña.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label className="text-white">Nombre *</Label>
                <Input value={formData.name} onChange={(e) => setFormData(p => ({...p, name: e.target.value}))} 
                  className="bg-[#1a1a1a] border-white/10 text-white" placeholder="Nombre" required />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Apellido *</Label>
                <Input value={formData.last_name} onChange={(e) => setFormData(p => ({...p, last_name: e.target.value}))} 
                  className="bg-[#1a1a1a] border-white/10 text-white" placeholder="Apellido" required />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Correo Electrónico *</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData(p => ({...p, email: e.target.value}))} 
                  className="bg-[#1a1a1a] border-white/10 text-white" placeholder="correo@ejemplo.com" required />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="border-white/10 text-white">Cancelar</Button>
                <Button type="submit" className="bg-[#D4AF37] text-black hover:bg-[#FFE082]" disabled={sending}>
                  {sending ? "Enviando..." : <><Send size={16} className="mr-2" />Enviar Invitación</>}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default TeamPage;
